"use client";

import React from "react";
import ErrorPage from "./ErrorPage";
import { logClientSideError } from "@/utils/logging/clientErrorLogger";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: (Error & { digest?: string }) | null;
  componentStack: string | null;
  hasLogged: boolean;
}

/**
 * Custom class-based Error Boundary that captures React's componentStack.
 *
 * React's componentDidCatch provides errorInfo.componentStack which shows
 * the component tree hierarchy leading to the error.
 *
 * Logging is done HERE in componentDidCatch — not in ErrorPage — to prevent
 * duplicate logs. ErrorPage is rendered with `skipLogging` flag.
 */
class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      componentStack: null,
      hasLogged: false,
    };
  }

  static getDerivedStateFromError(
    error: Error,
  ): Partial<ErrorBoundaryState> {
    return { hasError: true, error: error as Error & { digest?: string } };
  }

  componentDidCatch(_error: Error, errorInfo: React.ErrorInfo): void {
    const componentStack = errorInfo.componentStack || null;

    this.setState({ componentStack });

    // Log ONCE here with the full component stack
    if (!this.state.hasLogged && this.state.error) {
      logClientSideError(this.state.error, componentStack || undefined).catch(
        () => {
          // Silently fail
        },
      );
      this.setState({ hasLogged: true });
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      componentStack: null,
      hasLogged: false,
    });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <ErrorPage
          error={this.state.error}
          reset={this.handleReset}
          componentStack={this.state.componentStack || undefined}
          skipLogging
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
